import React, { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Layers, Search, ChevronDown, ChevronRight,
  CheckCircle, XCircle, AlertTriangle, Download,
  Smartphone, BarChart2, RefreshCw, ExternalLink, Copy
} from 'lucide-react';

// ─── Category colour palette ──────────────────────────────────────────────────
const CATEGORY_COLORS = [
  'var(--cyan)', 'var(--green)', 'var(--amber)', 'var(--purple)',
  '#f97316', '#ec4899', '#14b8a6', '#8b5cf6', '#06b6d4', '#84cc16',
  '#f43f5e', '#a78bfa', '#34d399', '#fb923c', '#60a5fa', '#e879f9',
];

function getCategoryColor(index) {
  return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
}

// Copy a set of apps' bundle IDs to the clipboard
function copyBundleIds(apps, suffix = '') {
  const ids = (apps || []).map(a => a.bundleId).filter(Boolean);
  if (!ids.length) { toast.error('Nothing to copy'); return; }
  navigator.clipboard.writeText(ids.join('\n'))
    .then(() => toast.success(`Copied ${ids.length} bundle ID${ids.length !== 1 ? 's' : ''}${suffix}`))
    .catch(() => toast.error('Copy failed'));
}

// ─── Platform badge ───────────────────────────────────────────────────────────
function PlatformBadge({ platform }) {
  if (platform === 'ios') return <span className="badge badge-cyan" style={{ fontSize: '9px' }}>iOS</span>;
  if (platform === 'android') return <span className="badge badge-green" style={{ fontSize: '9px' }}>Android</span>;
  return null;
}

// ─── Category segment card ────────────────────────────────────────────────────
function CategoryCard({ segment, index, totalApps, forceExpand }) {
  const [expanded, setExpanded] = useState(index < 3); // expand top 3 by default
  const isOpen = forceExpand || expanded;
  const color = getCategoryColor(index);
  const pct = Math.round((segment.count / totalApps) * 100);

  return (
    <div style={{
      background: 'var(--bg-card)', borderRadius: 'var(--radius)',
      border: `1px solid var(--border)`, marginBottom: '10px', overflow: 'hidden'
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px', cursor: 'pointer',
          background: 'var(--bg-elevated)',
          borderBottom: isOpen ? '1px solid var(--border)' : 'none'
        }}
      >
        {/* Color dot */}
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />

        {isOpen
          ? <ChevronDown size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          : <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}

        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', flex: 1 }}>
          {segment.category}
        </span>

        {/* Progress bar + count */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
          <div style={{ width: '120px', height: '4px', background: 'var(--bg-base)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '2px', transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '36px', textAlign: 'right' }}>{pct}%</span>
          <span className="badge badge-muted">{segment.count} app{segment.count !== 1 ? 's' : ''}</span>
          <button
            className="btn btn-secondary btn-sm"
            title="Copy this category's bundle IDs"
            onClick={(e) => { e.stopPropagation(); copyBundleIds(segment.apps, ` - ${segment.category}`); }}
            style={{ padding: '4px 8px' }}
          >
            <Copy size={11} />
          </button>
        </div>
      </div>

      {/* App list */}
      {isOpen && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Bundle ID</th>
                <th>App Name</th>
                <th>Developer</th>
                <th>Platform</th>
                <th>Price</th>
                <th>Rating</th>
                <th>Store</th>
              </tr>
            </thead>
            <tbody>
              {segment.apps.map((app, i) => (
                <tr key={i}>
                  <td className="mono" style={{ color: 'var(--cyan)', fontSize: '11px' }}>{app.bundleId}</td>
                  <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {app.iconUrl && (
                        <img src={app.iconUrl} alt="" style={{ width: 20, height: 20, borderRadius: '4px', flexShrink: 0 }} />
                      )}
                      {app.appName || '—'}
                    </div>
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{app.developerName || '—'}</td>
                  <td><PlatformBadge platform={app.platform} /></td>
                  <td style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{app.price || '—'}</td>
                  <td>
                    {app.rating
                      ? <span className="badge badge-amber" style={{ fontSize: '10px' }}>★ {app.rating}</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>}
                  </td>
                  <td>
                    {app.storeUrl && (
                      <a href={app.storeUrl} target="_blank" rel="noopener noreferrer"
                        style={{ color: 'var(--cyan)', display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px' }}>
                        View <ExternalLink size={10} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AppCategorySegmenter() {
  const [input, setInput] = useState('');
  const [concurrency, setConcurrency] = useState(5);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [query, setQuery] = useState('');

  const totalRef = useRef(0);

  async function handleSegment() {
    const ids = input.split('\n').map(l => l.trim()).filter(Boolean);
    if (ids.length === 0) return toast.error('Enter at least one bundle ID');
    if (ids.length > 500) return toast.error('Maximum 500 bundle IDs');

    setLoading(true); setResult(null); setProgress(0);
    totalRef.current = ids.length;

    try {
      const { data } = await axios.post('/api/app-category/segment', {
        bundleIds: ids,
        concurrency: parseInt(concurrency)
      });
      setResult(data);
      toast.success(`Segmented ${data.summary.categorized} apps into ${data.summary.totalCategories} categories`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Segmentation failed');
    } finally {
      setLoading(false);
      setProgress(100);
    }
  }

  function exportCSV() {
    if (!result) return;
    const rows = [['Bundle ID', 'App Name', 'Developer', 'Platform', 'Primary Category', 'All Categories', 'Price', 'Rating', 'Store URL']];
    result.segments.forEach(seg => {
      seg.apps.forEach(app => {
        rows.push([
          app.bundleId, app.appName || '', app.developerName || '',
          app.platform, seg.category,
          (app.allCategories || []).join(' | '),
          app.price || '', app.rating || '', app.storeUrl || ''
        ]);
      });
    });
    result.failed.forEach(app => {
      rows.push([app.bundleId, '', '', app.platform || '', 'FAILED', '', '', '', '']);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `app-categories-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success('Exported!');
  }

  const inputCount = input.split('\n').filter(l => l.trim()).length;

  // Search filter across all result categories
  const q = query.trim().toLowerCase();
  const matchApp = a =>
    (a.bundleId || '').toLowerCase().includes(q) ||
    (a.appName || '').toLowerCase().includes(q) ||
    (a.developerName || '').toLowerCase().includes(q);
  const filteredSegments = !result
    ? []
    : (!q
        ? result.segments
        : result.segments
            .map(seg => { const apps = seg.apps.filter(matchApp); return { ...seg, apps, count: apps.length }; })
            .filter(seg => seg.apps.length > 0));
  const matchCount = filteredSegments.reduce((n, sg) => n + sg.apps.length, 0);
  const allFilteredApps = filteredSegments.flatMap(sg => sg.apps);

  return (
    <div>
      <h1 className="page-title">App Category Segmenter</h1>
      <p className="page-subtitle">
        Paste a list of iOS or Android bundle IDs — the tool fetches each app's category from the store and groups them into segments
      </p>

      {/* Input card */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div className="card-title"><Layers size={14} /> Bundle IDs</div>
          <span className="badge badge-muted" style={{ fontSize: '10px' }}>Max 500</span>
        </div>

        <textarea
          className="input-field w-full"
          style={{ minHeight: '180px', fontSize: '12px' }}
          placeholder={'One bundle ID per line:\n\niOS (numeric):       1473573813\nAndroid (reverse):   com.king.candycrushsaga\nAndroid:             com.supercell.clashofclans'}
          value={input}
          onChange={e => setInput(e.target.value)}
        />

        <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={handleSegment} disabled={loading} style={{ minWidth: '160px' }}>
            {loading
              ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Fetching categories...</>
              : <><Search size={14} /> Segment by Category</>}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select className="select-field" value={concurrency} onChange={e => setConcurrency(e.target.value)}>
              {[2, 5, 10].map(n => <option key={n} value={n}>Concurrency: {n}</option>)}
            </select>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              {concurrency <= 2 && 'Slower but stable'}
              {concurrency == 5 && '⭐ Recommended'}
              {concurrency >= 10 && 'Fast — may hit rate limits'}
            </span>
          </div>

          {inputCount > 0 && (
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {inputCount} bundle ID{inputCount !== 1 ? 's' : ''}
            </span>
          )}

          {result && (
            <button className="btn btn-secondary btn-sm" onClick={exportCSV} style={{ marginLeft: 'auto' }}>
              <Download size={12} /> Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      {result && (
        <>
          {/* Summary stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            {[
              { label: 'Total Apps',       value: result.summary.total,            color: 'cyan'   },
              { label: 'Categories Found', value: result.summary.totalCategories,   color: 'green'  },
              { label: 'Categorized',      value: result.summary.categorized,       color: 'green'  },
              { label: 'Uncategorized',    value: result.summary.uncategorized,     color: 'amber'  },
              { label: 'iOS Apps',         value: result.summary.ios,               color: 'cyan'   },
              { label: 'Android Apps',     value: result.summary.android,           color: 'green'  },
              { label: 'Failed',           value: result.summary.failed,            color: 'red'    },
            ].map(({ label, value, color }) => (
              <div key={label} className={`stat-card ${color}`}>
                <div className="stat-label">{label}</div>
                <div className={`stat-value ${color}`}>{value}</div>
              </div>
            ))}
          </div>

          {/* Visual distribution bar */}
          {result.segments.length > 0 && (
            <div className="card" style={{ marginBottom: '20px' }}>
              <div className="card-title" style={{ marginBottom: '14px' }}>
                <BarChart2 size={14} /> Category Distribution
              </div>
              {/* Stacked bar */}
              <div style={{ display: 'flex', height: '28px', borderRadius: '6px', overflow: 'hidden', marginBottom: '14px' }}>
                {result.segments.map((seg, i) => {
                  const pct = (seg.count / result.summary.total) * 100;
                  return (
                    <div key={i} title={`${seg.category}: ${seg.count} apps (${Math.round(pct)}%)`}
                      style={{ width: `${pct}%`, background: getCategoryColor(i), transition: 'width 0.4s', minWidth: pct > 0 ? '2px' : '0' }} />
                  );
                })}
              </div>
              {/* Legend */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {result.segments.map((seg, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '2px', background: getCategoryColor(i), flexShrink: 0 }} />
                    {seg.category} <span style={{ color: 'var(--text-muted)' }}>({seg.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search within results */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '240px' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                className="input-field"
                style={{ width: '100%', paddingLeft: '30px', fontSize: '12px' }}
                placeholder="Search results by bundle ID, app name or developer..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
            {q && (
              <>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {matchCount} match{matchCount !== 1 ? 'es' : ''} in {filteredSegments.length} categor{filteredSegments.length !== 1 ? 'ies' : 'y'}
                </span>
                <button className="btn btn-secondary btn-sm" onClick={() => copyBundleIds(allFilteredApps, ' (search results)')}>
                  <Copy size={12} /> Copy matches
                </button>
              </>
            )}
          </div>

          {/* Category cards */}
          {filteredSegments.length === 0 && q ? (
            <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', padding: '24px' }}>
              No apps match "{query.trim()}".
            </div>
          ) : (
            filteredSegments.map((seg, i) => (
              <CategoryCard
                key={seg.category}
                segment={seg}
                index={i}
                totalApps={result.summary.total}
                forceExpand={!!q}
              />
            ))
          )}

          {/* Failed */}
          {result.failed.length > 0 && (
            <div className="card" style={{ borderColor: 'color-mix(in srgb, var(--red) 25%, transparent)', marginTop: '12px' }}>
              <div className="card-title" style={{ marginBottom: '10px' }}>
                <XCircle size={14} style={{ color: 'var(--red)' }} /> Failed Lookups
                <span className="badge badge-red" style={{ marginLeft: '6px' }}>{result.failed.length}</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {result.failed.map((f, i) => (
                  <div key={i} style={{ background: 'var(--bg-elevated)', border: '1px solid color-mix(in srgb, var(--red) 20%, transparent)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: '11px' }}>
                    <code style={{ color: 'var(--red)' }}>{f.bundleId}</code>
                    <span style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>{f.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
